/* eslint-disable import/no-extraneous-dependencies */
import { Box } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Tab as TabUnstyled, tabClasses as tabUnstyledClasses } from "@mui/base/Tab";
import { TabPanel as TabPanelUnstyled } from "@mui/base/TabPanel";
import { TabsList as TabsListUnstyled } from "@mui/base/TabsList";
import { styled } from "@mui/system";
import { colors, fonts } from "@zapalacorp/tokens";
import { getBrandCustom } from "../../../util/getCustomColor";

const styles = makeStyles(() => ({
  modalTabs: {
    width: "100%",
  },
  activityButton: {
    "& svg": {
      height: "20px",
      width: "100%",
    },
    "& span": {
      whiteSpace: "nowrap",
    },
  },
  statusWrapper: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    "& > div": {
      width: 120,
    },
  },
  statusField: {
    width: 120,
    height: 32,
  },

  tabError: {
    color: "#D92D20 !important",
    backgroundColor: "#FEF3F2 !important",
  },

  iconMoreOptions: {
    backgroundColor: "#E0F2FE",
    width: 32,
    height: 32,
    borderRadius: 8,
    fontSize: 20,
    padding: 0,
    "&:hover": {
      backgroundColor: "#E0F2FE",
    },
  },

  menuContainer: {
    width: 280,
    maxHeight: 190,
    marginTop: "4px",
    borderRadius: 12,
    backgroundColor: "#fff",
    boxShadow: " 2px 1px 5px rgba(152, 162, 179, 0.15), 0px 4px 10px rgba(48, 140, 219, 0.05)",
    "& > ul": {
      padding: "10px 5px !important",
    },
  },

  menu: {
    "& .MuiPaper-root": {
      backgroundColor: "#fff",
      top: "183px !important",
      "& li": {
        padding: "8px 16px",
        font: "500 12px/24px Manrope",
        color: "#465869",
        "&:not(:last-child)": {
          marginBottom: 8,
        },
        "&:hover": {
          backgroundColor: "#EDF5FB",
        },
      },
    },
    "& .MuiList-padding": {
      padding: "0px",
    },
  },

  menuItemSave: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  circularProgress: {
    width: "20px !important",
    height: "20px !important",
    color: "#465869",
  },
  actionsButtons: {
    paddingTop: "0px !important",
  },
  removePaddingBottom: {
    paddingBottom: "0px !important",
  },
  tabList: {
    // TODO: JOAO uncoment this when new tab design is being implemented
    // paddingLeft: "20px",
    // paddingRight: "20px",
  },
  tabPanel: {
    borderRadius: "12px",
  },
}));

const Tab = styled(TabUnstyled)({
  fontFamily: "Manrope",
  color: colors.gray800,
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: 700,
  lineHeight: "20px",
  letterSpacing: 0.1,

  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  whiteSpace: "nowrap",
  height: "44px !important",
  padding: "12px 16px",
  marginRight: "2px",
  backgroundColor: "#FFFFFF",
  borderRadius: "8px 8px 0px 0px",
  border: "none !important",
  "&:hover": {
    backgroundColor: getBrandCustom("headerBackgroundColor", "#0055A414"),
  },
  "@media screen and (max-width: 520px)": {
    backgroundColor: colors.white,
    color: colors.gray500,
  },
  [`&.${tabUnstyledClasses.selected}`]: {
    backgroundColor: getBrandCustom("headerBackgroundColor", "#0055A414"),
    color: getBrandCustom("primaryColor", colors.brandBlue500),
    border: "none !important",
    borderTopLeftRadius: "8px",
    borderTopRightRadius: "8px",
    fontFamily: fonts.default,
    fontSize: 14,
    fontWeight: 700,
    "@media screen and (max-width: 520px)": {
      backgroundColor: colors.white,
      color: getBrandCustom("primaryColor", colors.brandBlue500),
      borderBottom: `2px solid ${getBrandCustom("primaryColor", colors.brandBlue500)} !important`,
    },
  },
  [`&.${tabUnstyledClasses.disabled}`]: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
});

type TabPanelProps = {
  skipContainerPadding?: boolean;
  flexContainer?: boolean;
  rounded?: boolean;
  backgroundColor?: string;
};

const TabPanel = styled(TabPanelUnstyled, {
  shouldForwardProp: (props) =>
    props !== "skipContainerPadding" &&
    props !== "flexContainer" &&
    props !== "rounded" &&
    props !== "backgroundColor",
})<TabPanelProps>(({ rounded, skipContainerPadding, flexContainer, backgroundColor = "#FFF" }) => {
  return {
    width: "100%",
    fontFamily: "Manrope !important",
    border: "1px solid #E4E7EC",
    backgroundColor,
    height: "100%",
    borderRadius: rounded ? "12px" : undefined,
    padding: skipContainerPadding ? "0px !important" : "16px",
    ...(flexContainer
      ? {
          display: "flex",
          flexDirection: "column",
        }
      : undefined),
  };
});

const TabsList = styled(TabsListUnstyled, {
  shouldForwardProp(propName) {
    return propName !== "useInitialPadding" && propName !== "customPaddingRight";
  },
})<{ useInitialPadding?: boolean }>(({ useInitialPadding }) => ({
  display: "flex",
  justifyContent: "flex-start",
  alignContent: "flex-start",
  borderBottom: "none",
  paddingLeft: useInitialPadding ? "24px" : undefined,
  paddingRight: useInitialPadding ? "24px" : undefined,

  "@media screen and (max-width: 520px)": {
    overflowY: "auto",
    borderBottom: `2px solid ${colors.gray200} !important`,
    "&::-webkit-scrollbar": {
      width: 1,
      backgroundColor: colors.gray100,
      border: "none",
    },
    "&::-webkit-scrollbar-thumb": {
      width: 1,
      backgroundColor: colors.gray50,
      borderRadius: 0,
      border: "none",
    },
  },
}));

const Badge = styled(Box)({
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  padding: "15px",
  height: "20px",
  width: "20px",
  background: getBrandCustom("primaryColor", "#38CFC4"),
  borderRadius: "16px",
  marginLeft: "12px",
  fontFamily: "Manrope !important",
  fontWeight: 600,
  fontSize: "10px",
  color: "#FFF",
});

export { Badge, Tab, TabPanel, TabsList };
export default styles;
